import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

import { useBoardViewportTouchGestures } from "./boardTouchGestures";
import type { BoardPinchZoomUpdate } from "./boardTouchScroll";
import type { BoardTouchTap } from "./boardTouchTapTracking";
import { MIN_ZOOM } from "./boardViewport";

function useApplyPendingPinchScroll({
  zoom,
  viewportRef,
  pendingScrollRef,
}: {
  zoom: number;
  viewportRef: RefObject<HTMLDivElement | null>;
  pendingScrollRef: RefObject<{ scrollLeft: number; windowScrollY: number } | null>;
}): void {
  useLayoutEffect(() => {
    const pending = pendingScrollRef.current;
    if (!pending) return;
    pendingScrollRef.current = null;
    const viewport = viewportRef.current;
    if (viewport) viewport.scrollLeft = pending.scrollLeft;
    window.scrollTo({ top: pending.windowScrollY, behavior: "auto" });
  }, [zoom, viewportRef, pendingScrollRef]);
}

function useBoardPinchZoomSession({
  enabled,
  initialZoom,
  zoomSessionKey,
}: {
  enabled: boolean;
  initialZoom: number;
  zoomSessionKey: number;
}): {
  zoom: number;
  getZoom: () => number;
  getMinZoom: () => number;
  onZoom: (update: BoardPinchZoomUpdate) => void;
  pendingScrollRef: RefObject<{ scrollLeft: number; windowScrollY: number } | null>;
} {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const sessionMinZoomRef = useRef(MIN_ZOOM);
  const pendingScrollRef = useRef<{ scrollLeft: number; windowScrollY: number } | null>(null);
  const userAdjustedZoomRef = useRef(false);

  useEffect(() => {
    userAdjustedZoomRef.current = false;
  }, [zoomSessionKey]);

  useLayoutEffect(() => {
    if (!enabled || userAdjustedZoomRef.current) return;
    if (!Number.isFinite(initialZoom) || initialZoom <= 0) return;
    sessionMinZoomRef.current = Math.max(MIN_ZOOM, initialZoom);
    setZoom(initialZoom);
  }, [enabled, initialZoom, zoomSessionKey]);

  const getZoom = useCallback(() => zoomRef.current, []);
  const getMinZoom = useCallback(() => sessionMinZoomRef.current, []);
  const onZoom = useCallback((update: BoardPinchZoomUpdate) => {
    userAdjustedZoomRef.current = true;
    pendingScrollRef.current = {
      scrollLeft: update.scrollLeft,
      windowScrollY: update.windowScrollY,
    };
    setZoom(update.zoom);
  }, []);

  return { zoom, getZoom, getMinZoom, onZoom, pendingScrollRef };
}

export function useBoardPinchZoom({
  viewportRef,
  canvasRef,
  enabled,
  onTap,
  initialZoom,
  zoomSessionKey,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  enabled: boolean;
  onTap?: (tap: BoardTouchTap) => void;
  initialZoom: number;
  zoomSessionKey: number;
}): number {
  const { zoom, getZoom, getMinZoom, onZoom, pendingScrollRef } = useBoardPinchZoomSession({
    enabled,
    initialZoom,
    zoomSessionKey,
  });

  useBoardViewportTouchGestures({
    viewportRef,
    canvasRef,
    enabled,
    getZoom,
    getMinZoom,
    onTap,
    onZoom,
  });

  useApplyPendingPinchScroll({ zoom, viewportRef, pendingScrollRef });

  return enabled ? zoom : 1;
}
